import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { RefreshCw } from "lucide-react";
import type { ConcurrencySummary } from "@/api/usage-concurrency";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import type { AccessTokenGetter } from "@/lib/utils";

export type ConcurrencyLoadState =
  | { status: "loading" }
  | { status: "ready"; data: ConcurrencySummary }
  | { status: "unsupported" | "forbidden" | "error" };

function UsageIntensityChart({ summary, userLabels }: { summary: ConcurrencySummary; userLabels: Map<string, string> }) {
  const { t } = useTranslation();
  const selected = [...summary.series].sort((a, b) =>
    Math.max(0, ...b.points.map((point) => point.maxConcurrent)) - Math.max(0, ...a.points.map((point) => point.maxConcurrent))
    || a.userId.localeCompare(b.userId)).slice(0, 8);
  const config: ChartConfig = Object.fromEntries(selected.map((series, index) => [
    `user${index}`, { label: userLabels.get(series.userId) ?? series.userId, color: `hsl(var(--chart-${index % 5 + 1}))` },
  ]));
  const start = new Date(`${summary.range.startDate}T00:00:00Z`).getTime();
  const end = Math.min(Date.now(), new Date(`${summary.range.endDateExclusive}T00:00:00Z`).getTime());
  const values = selected.map((series) => new Map(series.points.map((point) => [point.hour, point.maxConcurrent])));
  const data: Array<Record<string, string | number>> = [];
  for (let time = start; time < end; time += 3_600_000) {
    const hour = new Date(time).toISOString().slice(0, 13) + ":00:00Z";
    const row: Record<string, string | number> = { hour };
    values.forEach((points, index) => { row[`user${index}`] = points.get(hour) ?? 0; });
    data.push(row);
  }
  const hasData = summary.series.some((series) => series.points.length > 0);
  return <section className="min-w-0 space-y-3 rounded-xl border bg-card p-4 shadow-sm sm:p-5" aria-labelledby="usage-intensity-heading">
    <div>
      <h2 id="usage-intensity-heading" className="text-lg font-semibold">{t("usage.intensity")}</h2>
      <p className="text-xs text-muted-foreground">{t("usage.intensityDescription")}</p>
    </div>
    {hasData ? <ChartContainer config={config} className="h-[300px] min-h-[300px] w-full min-w-0" role="img" aria-label={t("usage.chartLabel")}>
      <LineChart accessibilityLayer data={data} margin={{ top: 8, right: 8, bottom: 6, left: -20 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="hour" tickLine={false} axisLine={false} minTickGap={32} interval="preserveStartEnd" tickFormatter={(value: string) => new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", timeZone: "UTC" })} />
        <YAxis allowDecimals={false} domain={[0, "dataMax"]} tickLine={false} axisLine={false} />
        <ChartTooltip content={<ChartTooltipContent labelFormatter={(value) => new Date(String(value)).toLocaleString(undefined, { timeZone: "UTC", timeZoneName: "short" })} />} />
        {selected.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
        {selected.map((series, index) => <Line key={series.userId} type="stepAfter" dataKey={`user${index}`} name={userLabels.get(series.userId) ?? series.userId} stroke={`var(--color-user${index})`} strokeWidth={2} dot={false} isAnimationActive={false} />)}
      </LineChart>
    </ChartContainer> : <p className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">{t("usage.empty")}</p>}
    {summary.series.length > selected.length && <p className="text-xs text-muted-foreground">{t("usage.lineLimit", { shown: selected.length, total: summary.series.length })}</p>}
    {summary.unattributedRunCount > 0 && <p className="text-xs text-muted-foreground">{t("usage.unattributed", { count: summary.unattributedRunCount })}</p>}
  </section>;
}

export function ConcurrencySection({ state, onRetry, getToken }: { state: ConcurrencyLoadState; onRetry: () => void; getToken: AccessTokenGetter }) {
  const { t } = useTranslation();
  const { tenantUsers } = useTenantUsers(getToken);
  const userLabels = new Map(tenantUsers.map((user) => [user.id, user.display_name ?? user.email ?? user.id]));
  if (state.status === "ready") return <UsageIntensityChart summary={state.data} userLabels={userLabels} />;
  return <section className="min-w-0 space-y-3 rounded-xl border bg-card p-5 shadow-sm" aria-labelledby="usage-intensity-heading">
    <h2 id="usage-intensity-heading" className="text-lg font-semibold">{t("usage.intensity")}</h2>
    {state.status === "loading" ? <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="size-4" />{t("usage.loading")}</div>
      : <div role="alert" className="space-y-2 text-sm text-muted-foreground">
        <p>{state.status === "unsupported" ? t("usage.unsupported") : state.status === "forbidden" ? t("usage.forbidden") : t("usage.loadFailed")}</p>
        {state.status === "error" && <Button variant="outline" size="sm" onClick={onRetry}><RefreshCw className="mr-2 size-4" />{t("costs.retry")}</Button>}
      </div>}
  </section>;
}
